import React, { useContext, useMemo, useState } from 'react';

import type { GetBioacousticsSampleDataQuery } from '@/__generated__/graphql.js';

type BioacousticsEntityData = GetBioacousticsSampleDataQuery['getBioacousticsSampleData']
import { myhiveLocalization, MyHiveLocalizationKey } from '@/localization/my-hive.js';
import { LanguageContext } from '@/providers/LanguageProvider.js';

import { PlayPauseIcon, useAudioPlayer } from './AudioPlayer.js';
import { BioacousticsDailyChart, buildChartData, type ChartRow } from './BioacousticsDailyChart.js';
import { HillTooltip } from './BioacousticsTooltip.js';
import {
  computeDateRange,
  computeMostRecentDate,
  computeTopSpecies,
  formatLargeNumber,
  hillDiversityQ1,
  type TopSpeciesEntry,
} from './bioacousticsUtils.js';

export type { TopSpeciesEntry };

export function BioacousticsHeroTile({ data, showLinks = true }: { data: BioacousticsEntityData | undefined | null; showLinks?: boolean }): React.JSX.Element {
  const { language } = useContext(LanguageContext);
  const t = myhiveLocalization[language];
  const [hoveredRow, setHoveredRow] = useState<ChartRow | null>(null);
  const { playingTaxa, handlePlay } = useAudioPlayer();

  const { endDate, chartCutoff } = useMemo(() => {
    const mostRecent = computeMostRecentDate(data?.dailyPollinatorData ?? []);
    return computeDateRange(mostRecent);
  }, [data]);

  const chartRows = data
    ? buildChartData(data).filter(r => r.date >= chartCutoff && r.date <= endDate)
    : [];
  const buzzValues = chartRows.map(r => r.meanBuzzPerHour).filter((v): v is number => v != null);
  const displayRow = hoveredRow ?? [...chartRows].reverse().find(r => r.meanBuzzPerHour != null) ?? chartRows[chartRows.length - 1] ?? null;
  const chartRangeMin = buzzValues.length ? Math.min(...buzzValues) : 0;
  const chartRangeMax = buzzValues.length ? Math.max(...buzzValues) : 0;
  const chartRangeMean = buzzValues.length ? buzzValues.reduce((s, v) => s + v, 0) / buzzValues.length : 0;

  const uniqueBirdSpecies = useMemo((): string => {
    if (!data) return '—';
    const count = new Set(data.birdObservations.map(o => o.latinTaxa).filter(Boolean)).size;
    return count === 0 ? '—' : String(count);
  }, [data]);

  const birdStats = useMemo(() => {
    if (!data) return { detections: '—' };
    return { detections: formatLargeNumber(data.birdObservations.length) };
  }, [data]);

  const insectDiversity = useMemo((): { score: string; change: string | null; up: boolean } => {
    if (!data) return { score: '—', change: null, up: true };
    const now = Date.now();
    const week = 7 * 24 * 60 * 60 * 1000;
    const countFor = (from: number, to: number): Map<string, number> => {
      const m = new Map<string, number>();
      for (const obs of data.hourlyPollinatorObservations) {
        const t = new Date(obs.date).getTime() + obs.hour * 3_600_000;
        if (t < from || t >= to) continue;
        m.set(obs.hz, (m.get(obs.hz) ?? 0) + obs.count);
      }
      return m;
    };
    const thisWeek = countFor(now - week, now);
    const prevWeek = countFor(now - 2 * week, now - week);
    if (thisWeek.size === 0) return { score: '—', change: null, up: true };
    const current = hillDiversityQ1(Array.from(thisWeek.values()));
    const score = current.toFixed(1);
    if (prevWeek.size === 0) return { score, change: null, up: true };
    const prev = hillDiversityQ1(Array.from(prevWeek.values()));
    const pct = ((current - prev) / prev) * 100;
    const up = pct >= 0;
    return { score, change: `${up ? '+' : ''}${pct.toFixed(1)}%`, up };
  }, [data]);

  const topSpecies = useMemo(() => data ? computeTopSpecies(data.birdObservations) : [], [data]);

  return (
    <div className="flex flex-col sm:flex-row flex-1 min-h-[390px] relative">

      {/* Left: Pollinators */}
      <div className="flex-1 flex flex-col p-5 min-w-0 sm:mr-80">
        <div className="flex items-baseline justify-between mb-3 shrink-0">
          <span className="font-inter text-xl text-gray-900 flex items-center gap-2">
            {t[MyHiveLocalizationKey.BioacousticsPollinators]}
            <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--color-brand-warm-orange-500)' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-brand-warm-orange-500 animate-pulse" />
              {t[MyHiveLocalizationKey.BioacousticsLive]}
            </span>
          </span>
          <span className="text-xs uppercase tracking-widest text-gray-400">{t[MyHiveLocalizationKey.BioacousticsLast14Days]}</span>
        </div>

        <div className="h-[180px] sm:flex-1 sm:h-auto">
          <BioacousticsDailyChart
            data={data ?? undefined}
            metric="buzz"
            startDate={chartCutoff}
            endDate={endDate}
            syncId="pollinator"
            showWeather={false}
            showTrend={true}
            showTooltip={false}
            onHoverRow={setHoveredRow}
          />
        </div>

        {/* Stats tile */}
        <div className="@container shrink-0 rounded-xl px-4 py-3" style={{ backgroundColor: 'var(--color-brand-teal-dark-500)' }}>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="shrink-0 w-full @sm:w-auto pb-2 @sm:pb-0 border-b @sm:border-b-0 border-white/10">
              <div className="flex items-center gap-3 @sm:block">
                <p className="text-sm font-medium text-white leading-tight whitespace-nowrap">
                  {displayRow ? new Date(displayRow.date + 'T00:00:00Z').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : '—'}
                </p>
                <p className="text-xs uppercase tracking-widest text-brand-teal-300">{hoveredRow ? t[MyHiveLocalizationKey.BioacousticsSelected] : t[MyHiveLocalizationKey.BioacousticsMostRecent]}</p>
              </div>
            </div>
            <div className="hidden @sm:block w-px self-stretch" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
            <div className="shrink-0">
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className="w-2 h-2 rounded-full bg-brand-warm-orange-500 shrink-0" />
                <span className="text-xs uppercase tracking-widest text-brand-teal-300">{t[MyHiveLocalizationKey.BioacousticsActivity]}</span>
              </div>
              <p className="text-3xl font-bold text-white leading-none">
                {displayRow?.meanBuzzPerHour != null ? displayRow.meanBuzzPerHour.toFixed(1) : '—'}
              </p>
              <p className="text-xs text-brand-teal-300">{t[MyHiveLocalizationKey.BioacousticsDetectionsPerHour]}</p>
            </div>
            <div className="w-px self-stretch" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
            <div className="flex-1 min-w-[100px]">
              <p className="text-xs uppercase tracking-widest text-brand-teal-300 mb-1.5">{t[MyHiveLocalizationKey.BioacousticsRangeOverPeriod]}</p>
              {chartRangeMax > chartRangeMin && displayRow?.meanBuzzPerHour != null ? (() => {
                const pct = (v: number): number => Math.max(0, Math.min(100, ((v - chartRangeMin) / (chartRangeMax - chartRangeMin)) * 100));
                return (
                  <div className="flex items-center gap-1.5 text-xs text-brand-teal-300">
                    <span>{chartRangeMin.toFixed(1)}</span>
                    <div className="relative flex-1 h-1.5 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
                      <div className="absolute top-0 left-0 h-full rounded-full" style={{ width: `${pct(displayRow.meanBuzzPerHour)}%`, backgroundColor: 'var(--color-brand-warm-orange-500)' }} />
                      <div className="absolute top-1/2 w-0.5 h-3 -translate-y-1/2" style={{ left: `${pct(chartRangeMean)}%`, backgroundColor: 'var(--color-brand-warm-orange-400)', opacity: 0.6 }} />
                      <div className="absolute top-1/2 w-2.5 h-2.5 rounded-full border-2 -translate-x-1/2 -translate-y-1/2" style={{ left: `${pct(displayRow.meanBuzzPerHour)}%`, backgroundColor: 'var(--color-brand-warm-orange-500)', borderColor: 'var(--color-brand-teal-dark-500)' }} />
                    </div>
                    <span>{chartRangeMax.toFixed(1)}</span>
                  </div>
                );
              })() : (
                <div className="h-1.5 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.15)' }} />
              )}
            </div>
          </div>
        </div>

        {/* Pollinator Diversity Score */}
        <div className="mt-3 flex items-center gap-3 flex-wrap shrink-0">
          <HillTooltip
            label={t[MyHiveLocalizationKey.BioacousticsDiversityScore]}
            description={t[MyHiveLocalizationKey.BioacousticsDiversityScoreDescription]}
            footnote={t[MyHiveLocalizationKey.BioacousticsDiversityScoreFootnote]}
            labelColor="text-gray-600"
            iconColor="border-gray-400 text-gray-400"
          />
          <span className="text-sm font-bold text-gray-900">{insectDiversity.score} / 19</span>
          <div className="flex gap-0.5 flex-1 min-w-[80px]">
            {Array.from({ length: 19 }).map((_, i) => {
              const numScore = parseFloat(insectDiversity.score) || 0;
              const filled = i < Math.round(numScore);
              return (
                <div key={i} className="h-2.5 flex-1 rounded-sm" style={{ backgroundColor: filled ? 'var(--color-brand-teal-dark-500)' : '#e5e7eb' }} />
              );
            })}
          </div>
          {insectDiversity.change != null && (
            <span className="flex items-center gap-1 text-xs font-medium whitespace-nowrap" style={{ color: insectDiversity.up ? '#388E3C' : '#DB5012' }}>
              <span>{insectDiversity.up ? '↑' : '↓'}</span>
              <span>{insectDiversity.change}</span>
              <span className="text-gray-400 font-normal">{t[MyHiveLocalizationKey.BioacousticsVsPrevious7Days]}</span>
            </span>
          )}
        </div>
        {showLinks && (
          <div className="mt-2 shrink-0">
            <a href="#pollinators" className="text-xs text-gray-500 hover:text-gray-700">{t[MyHiveLocalizationKey.BioacousticsPollinatorBreakdownLink]}</a>
          </div>
        )}
      </div>

      {/* Right: Birds */}
      <div className="sm:absolute sm:right-0 sm:top-0 sm:bottom-0 sm:w-80 flex flex-col p-5 border-t sm:border-t-0 sm:border-l border-gray-100">
        <div className="flex items-baseline justify-between mb-3 shrink-0">
          <span className="font-inter text-xl text-gray-900 flex items-center gap-2">
            {t[MyHiveLocalizationKey.BioacousticsBirds]}
            <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--color-brand-warm-orange-500)' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-brand-warm-orange-500 animate-pulse" />
              {t[MyHiveLocalizationKey.BioacousticsLive]}
            </span>
          </span>
          <span className="text-xs uppercase tracking-widest text-gray-400">{t[MyHiveLocalizationKey.BioacousticsAllTime]}</span>
        </div>

        <div className="flex gap-6 mb-5 shrink-0 rounded-xl px-4 py-3" style={{ backgroundColor: 'var(--color-brand-teal-dark-500)' }}>
          <div>
            <p className="text-xs uppercase tracking-widest text-brand-teal-300 mb-1">{t[MyHiveLocalizationKey.BioacousticsUniqueBirdSpecies]}</p>
            <p className="text-4xl font-bold text-white">{uniqueBirdSpecies}</p>
          </div>
          <div className="w-px self-stretch" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
          <div>
            <p className="text-xs uppercase tracking-widest text-brand-teal-300 mb-1">{t[MyHiveLocalizationKey.BioacousticsTotalDetections]}</p>
            <p className="text-4xl font-bold text-white">{birdStats.detections}</p>
          </div>
        </div>

        <p className="text-xs uppercase tracking-widest text-gray-500 mb-3 shrink-0">{t[MyHiveLocalizationKey.BioacousticsMostHeard]}</p>

        <div className="flex flex-col gap-2 flex-1 min-h-0 overflow-hidden relative">
          {topSpecies.map((entry, index) => {
            const entryKey = entry.latinTaxa;
            const isPlaying = playingTaxa === entryKey;
            return (
              <div key={entryKey} className={`rounded-xl px-3 py-2 flex items-center gap-3${index >= 4 ? ' hidden sm:flex' : ''}`} style={{ backgroundColor: isPlaying ? 'rgba(219,80,18,0.08)' : '#f5f0e6' }}>
                <div className="relative shrink-0 w-10 h-10">
                  <div className="rounded-full overflow-hidden w-full h-full bg-gray-200">
                    {entry.imgUrl
                      ? <img src={entry.imgUrl} alt={entry.commonName ?? ''} className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center" style={{ color: '#d1d5db', fontSize: 16 }}>♦</div>
                    }
                  </div>
                  <button
                    onClick={() => handlePlay(entryKey, entry.audioClip)}
                    className="absolute bottom-0 right-0 w-4 h-4 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: isPlaying ? 'var(--color-brand-warm-orange-500)' : 'rgba(0,183,157,0.85)', cursor: entry.audioClip ? 'pointer' : 'default', opacity: entry.audioClip ? 1 : 0.3 }}
                    disabled={!entry.audioClip}
                  >
                    <PlayPauseIcon isPlaying={isPlaying} size={5} playFill="rgba(255,255,255,0.9)" />
                  </button>
                </div>
                <div className="flex-1 min-w-0">
                  {entry.commonName && <p className="text-xs font-semibold text-gray-900 leading-tight truncate">{entry.commonName}</p>}
                  {entry.latinTaxa && <p className="text-xs italic text-gray-500 leading-tight truncate">{entry.latinTaxa}</p>}
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-base font-bold text-gray-900">{entry.count.toLocaleString()}</p>
                  <p className="text-xs uppercase tracking-widest text-gray-400" style={{ fontSize: 9 }}>{t[MyHiveLocalizationKey.BioacousticsDetections]}</p>
                </div>
              </div>
            );
          })}
          {topSpecies.length === 0 && <p className="text-xs text-gray-400 py-4 text-center w-full">{t[MyHiveLocalizationKey.BioacousticsNoBirdData]}</p>}
          {topSpecies.length > 0 && (
            <div className="absolute bottom-0 left-0 right-0 h-10 pointer-events-none" style={{ background: 'linear-gradient(to bottom, transparent, white)' }} />
          )}
        </div>

        {showLinks && (
          <div className="mt-3 shrink-0">
            <a href="#birds" className="text-xs text-gray-500 hover:text-gray-700">{t[MyHiveLocalizationKey.BioacousticsDawnChorusLink]}</a>
          </div>
        )}
      </div>

    </div>
  );
}
